import React, { useState, useEffect } from 'react';
import { TbPlus } from 'react-icons/tb';
import { FiLogOut } from 'react-icons/fi';
import { BiFolderOpen } from 'react-icons/bi';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import { toast } from 'react-hot-toast';
import { IoPeople } from 'react-icons/io5';

import Modal from './components/base/Modal';
import ProjectMembersModal from './components/admin/ProjectMembersModal';
import { API_URL } from './services/api';
import { clearLocalStorageKeepingLoginHint } from './config/authStorageKeys';
import { REVOKED_NOTICE, takeRevokedNotice } from './services/projectAccess';

// `embedded`: shown inside the Account Settings window instead of as a page.
// `currentProjectId` marks the project the user is working in.
const ProjectsDemo = ({
  onProjectAccess,
  userIdToken,
  handleStorageChange,
  embedded = false,
  currentProjectId = null,
}) => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const selectedProject = null;
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [membersModalProject, setMembersModalProject] = useState(null);

  // Fetch Projects
  const fetchProjects = async () => {
    setLoading(true);
    setError('');

    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/userProjects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || errorData.message || 'Login failed.',
        );
      }
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (err) {
      setError('Unable to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // Create Project
  const handleCreateProject = async () => {
    setError('');
    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/projectCreate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_name: newProjectName,
          //   project_id: newProjectId,
        }),
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to create project! Status: ${response.status}`);
      }
      setIsModalOpen(false);
      setNewProjectName('');
      //   setNewProjectId("");
      fetchProjects(); // Refresh projects after successful creation
    } catch (err) {
      setError('Failed to create project. Please try again.');
    }
  };

  // Delete Project
  const handleDeleteProject = async (projectId) => {
    setError('');
    setDeleteLoading(true);
    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/projectDelete/${projectId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to delete project! Status: ${response.status}`);
      }
      fetchProjects(); // Refresh projects after successful deletion
    } catch (err) {
      setError('Failed to delete project. Please try again.');
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Delete Project
  const handleAccessProject = async (projectId) => {
    setError('');
    try {
      const token = userIdToken;
      const response = await fetch(`${API_URL}/projectLogin/${projectId}`, {
        // method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to delete project! Status: ${response.status}`);
      }
      const data = await response.json();
      onProjectAccess(data.token);

      //   fetchProjects(); // Refresh projects after successful deletion
    } catch (err) {
      setError('Failed to delete project. Please try again.');
    }
  };

  const handleLogout = () => {
    async function logout() {
      try {
        await fetch(`${API_URL}/logout`, {
          method: 'POST',
          credentials: 'include', // 🔥 important! send cookies with the request
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        toast.error(`Logout failed: ${error.message}`);
      } finally {
        clearLocalStorageKeepingLoginHint();
        handleStorageChange();
      }
    }
    logout();
  };

  useEffect(() => {
    fetchProjects();
    // Sent here because the project the user was in is no longer theirs.
    if (takeRevokedNotice()) toast.error(REVOKED_NOTICE, { duration: 6000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={
        embedded ? '' : 'bg-1 min-h-screen bg-gradient-to-br px-4 py-8'
      }
    >
      <div
        className={
          embedded
            ? ''
            : 'mx-auto max-w-5xl rounded-lg bg-zinc-900 p-6 shadow-lg'
        }
      >
        {embedded ? (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white">Projects</h3>
            <p className="mt-1 text-xs text-slate-400">
              Open a project to work in it. Your chats, files and dashboard
              change to that project.
            </p>
          </div>
        ) : (
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-3xl font-bold text-zinc-200">Projects</h1>
            <button
              onClick={handleLogout}
              className="flex items-center rounded-lg bg-zinc-700 px-4 py-2 font-bold text-white transition hover:bg-zinc-600"
            >
              <FiLogOut className="mr-2" size={20} /> Log Out
            </button>
          </div>
        )}
        {loading && <p className="text-center text-zinc-400">Loading...</p>}
        {error && <div className="mb-4 text-center text-red-500">{error}</div>}
        {projects.length === 0 && !loading && (
          <p className="text-center text-zinc-400">
            No projects found. Create one!
          </p>
        )}
        <button
          onClick={() => setIsModalOpen(true)}
          className={`flex items-center rounded-lg bg-gradient-to-b from-[#5566FF] to-[#333D99] font-bold text-white transition hover:opacity-90 ${
            embedded ? 'mb-4 px-3 py-1.5 text-xs' : 'mb-6 px-4 py-2'
          }`}
        >
          <TbPlus className="mr-2" size={embedded ? 16 : 20} /> Create Project
        </button>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <div
              key={project.project_id}
              className={
                embedded
                  ? 'rounded-lg border border-slate-700/60 bg-slate-800/50 p-3 text-white'
                  : 'transform rounded-lg bg-zinc-700 p-4 text-white shadow transition hover:scale-105'
              }
            >
              <h2
                className={`flex items-center gap-2 font-semibold ${
                  embedded ? 'text-sm' : 'text-xl'
                }`}
              >
                <span className="truncate">{project.project_name}</span>
                {project.project_id === currentProjectId && (
                  <span className="shrink-0 rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                    Current
                  </span>
                )}
              </h2>
              <p
                className={`truncate text-zinc-400 ${
                  embedded ? 'text-[11px]' : 'text-sm'
                }`}
              >
                ID: {project.project_id}
              </p>
              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  onClick={() => handleAccessProject(project.project_id)}
                  disabled={project.project_id === currentProjectId}
                  className={`flex items-center rounded-lg bg-gradient-to-b from-[#5566FF] to-[#333D99] font-bold text-white transition hover:opacity-90 disabled:cursor-default disabled:opacity-40 ${
                    embedded ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'
                  }`}
                >
                  <BiFolderOpen className="mr-2" size={embedded ? 16 : 20} />{' '}
                  Open project
                </button>
                <button
                  onClick={() => setMembersModalProject(project)}
                  title="Manage team"
                  className={`flex items-center rounded-lg bg-zinc-600 font-bold text-white transition hover:bg-zinc-500 ${
                    embedded ? 'px-2 py-1.5' : 'px-3 py-2'
                  }`}
                >
                  <IoPeople size={embedded ? 16 : 20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => setIsModalOpen(false)}
          title={'Create New Project'}
        >
          <div className="p-8">
            <input
              type="text"
              placeholder="Project Name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              className="w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-zinc-100 placeholder-zinc-500 transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
              required
            />

            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={handleCreateProject}
                className="flex-direction-row flex items-center rounded-md border 
                border-zinc-700 bg-zinc-800 px-4 py-2 text-zinc-300
                transition duration-200 hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-500"
              >
                <TbPlus className="mr-2" size={20} /> Create
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Project Warning modal */}
      {isDeleteModalOpen && selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="w-full max-w-md rounded-xl bg-zinc-800 p-6 text-sm text-white shadow-lg md:text-base">
            <div className="mb-4 flex items-center">
              <HiOutlineExclamationCircle
                className="mr-3 text-yellow-400"
                size={24}
              />
              <h2 className="text-lg font-bold md:text-xl">Delete Project</h2>
            </div>
            <p className="mb-6 text-zinc-300">
              Do you really want to delete{' '}
              <span className="font-semibold text-white">
                "{selectedProject.project_name}"
              </span>{' '}
              project?
            </p>
            <div className="flex justify-end gap-3 md:gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="rounded-lg border border-zinc-600 bg-zinc-700 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-zinc-600 md:px-4 md:py-2 md:text-base"
              >
                Cancel
              </button>
              <button
                Disabled={deleteLoading}
                onClick={() => {
                  handleDeleteProject(selectedProject.project_id);
                }}
                className={` rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-red-500 md:px-4 md:py-2 md:text-base ${deleteLoading && 'animate-pulse'} `}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {membersModalProject && (
        <ProjectMembersModal
          isOpen={true}
          onClose={() => setMembersModalProject(null)}
          projectId={membersModalProject.project_id}
          projectName={membersModalProject.project_name}
          userIdToken={userIdToken}
        />
      )}
    </div>
  );
};

export default ProjectsDemo;
